import type { Conversation } from "./types";

export type PilotScenario = {
  id: "incident" | "commercial" | "technical";
  title: string;
  eyebrow: string;
  description: string;
  outcome: string;
  build: () => Conversation;
};

export const pilotScenarios: PilotScenario[] = [
  {
    id: "incident",
    eyebrow: "OPERACIÓN CRÍTICA",
    title: "Robo y cámaras fuera de línea",
    description: "Simula un grupo de cliente que reporta un incidente urgente, solicita visita, responsables y un informe posterior.",
    outcome: "Prueba prioridad, compromiso, visita e informe con evidencia.",
    build: () => buildConversation({
      customer: "Cliente Demo Salud",
      site: "Sede Central",
      contactName: "Administración Demo",
      priority: "urgent",
      assignedTo: "Equipo Técnico Demo",
      tags: ["incidente", "cctv", "urgente"],
      summary: "El cliente ficticio reporta un ingreso no autorizado durante la noche y dos cámaras sin imagen. Se requiere visita urgente, conexión al monitoreo y un informe con acciones correctivas.",
      messages: [
        ["Administración Demo", "Buenos días. Anoche ingresaron por el acceso lateral y necesitamos revisar urgente las cámaras.", "inbound"],
        ["Administración Demo", "La cámara del portón y la del estacionamiento aparecen sin señal desde ayer.", "inbound"],
        ["Equipo Coordinación Demo", "Recibido. Coordinaremos visita hoy y dejaremos responsable confirmado antes de las 10:00.", "outbound"],
        ["Equipo Técnico Demo", "Puedo estar a las 15:30. Revisaré alimentación, enlaces y grabación del NVR.", "outbound"],
        ["Administración Demo", "Confirmado. Necesitamos además un informe y propuesta para voceo remoto.", "inbound"],
      ],
      commitments: [
        ["Confirmar y ejecutar visita técnica urgente", "Equipo Técnico Demo", "Hoy · 15:30", 1],
        ["Revisar cámara de portón, estacionamiento y grabación NVR", "Equipo Técnico Demo", "Durante la visita", 2],
        ["Enviar informe y propuesta de voceo remoto", "Equipo Coordinación Demo", "Mañana · 13:00", 4],
      ],
      nextAction: "Confirmar llegada del técnico y registrar diagnóstico de las dos cámaras.",
      visitRequired: true,
      reportRequired: true,
    }),
  },
  {
    id: "commercial",
    eyebrow: "VENTA CONSULTIVA",
    title: "Cotización de vigilancia nocturna",
    description: "Simula una conversación comercial con dos alternativas de servicio, vehículo, alimentación y fecha límite.",
    outcome: "Prueba oportunidad, responsable comercial y seguimiento de propuesta.",
    build: () => buildConversation({
      customer: "Cliente Demo Industrial",
      site: "Faena Norte",
      contactName: "Operaciones Demo",
      priority: "high",
      assignedTo: "Equipo Comercial Demo",
      tags: ["oportunidad", "guardias", "oferta"],
      summary: "El cliente solicita dos alternativas separadas de vigilancia: turno nocturno y cobertura continua, ambas con vehículo 4x4, alimentación y equipamiento.",
      messages: [
        ["Operaciones Demo", "Necesitamos propuesta para vigilancia nocturna 20:00 a 08:00 y otra alternativa 24/7.", "inbound"],
        ["Operaciones Demo", "Ambas deben considerar camioneta 4x4 estándar minero, alimentación y botón de pánico en garita.", "inbound"],
        ["Equipo Comercial Demo", "Prepararemos las dos alternativas por separado con dotación, equipamiento y valor mensual.", "outbound"],
        ["Operaciones Demo", "Favor incluir también el costo de sábados, domingos y festivos. Lo necesitamos el viernes antes de las 13:00.", "inbound"],
      ],
      commitments: [
        ["Preparar alternativa nocturna 12 horas", "Equipo Comercial Demo", "Viernes · 13:00", 0],
        ["Preparar alternativa continua 24/7", "Equipo Comercial Demo", "Viernes · 13:00", 0],
        ["Separar costo de sábados, domingos y festivos", "Equipo Costos Demo", "Jueves · 18:00", 3],
      ],
      nextAction: "Completar costeo interno y enviar ambas alternativas antes del viernes.",
      visitRequired: false,
      reportRequired: true,
    }),
  },
  {
    id: "technical",
    eyebrow: "SERVICIO TÉCNICO",
    title: "NVR con alarma y cámaras desconectadas",
    description: "Simula coordinación de diagnóstico técnico, revisión en terreno y cotización posterior.",
    outcome: "Prueba trazabilidad técnica y generación de reporte verificable.",
    build: () => buildConversation({
      customer: "Cliente Demo Logística",
      site: "Centro Sur",
      contactName: "Mesa Técnica Demo",
      priority: "high",
      assignedTo: "Equipo Técnico Demo",
      tags: ["mantenimiento", "nvr", "cotización"],
      summary: "El cliente informa una alarma en el NVR y dos cámaras desconectadas. Solicita visita técnica y cotización después del diagnóstico.",
      messages: [
        ["Mesa Técnica Demo", "Tenemos el NVR con una alarma y dos cámaras que aparecen desconectadas.", "inbound"],
        ["Mesa Técnica Demo", "Después de revisar, necesitamos que nos envíen la cotización de la reparación.", "inbound"],
        ["Equipo Técnico Demo", "Puedo revisar el viernes en la mañana. Necesito acceso al rack y a los puntos de cámara.", "outbound"],
        ["Mesa Técnica Demo", "Viernes 09:30 está bien. Dejaré autorizado el ingreso.", "inbound"],
      ],
      commitments: [
        ["Realizar visita y diagnóstico de NVR y cámaras", "Equipo Técnico Demo", "Viernes · 09:30", 0],
        ["Emitir cotización posterior al diagnóstico", "Equipo Comercial Demo", "Viernes · 18:00", 1],
      ],
      nextAction: "Confirmar acceso al rack y registrar evidencia del diagnóstico.",
      visitRequired: true,
      reportRequired: true,
    }),
  },
];

type BuildInput = {
  customer: string;
  site: string;
  contactName: string;
  priority: Conversation["priority"];
  assignedTo: string;
  tags: string[];
  summary: string;
  messages: Array<[sender: string, body: string, direction: "inbound" | "outbound"]>;
  commitments: Array<[description: string, responsible: string, dueAt: string, sourceIndex: number]>;
  nextAction: string;
  visitRequired: boolean;
  reportRequired: boolean;
};

function buildConversation(input: BuildInput): Conversation {
  const now = new Date();
  const unique = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${now.getTime()}-${Math.random().toString(36).slice(2)}`;
  const id = `pilot-${slug(input.customer)}-${unique}`;
  const messages = input.messages.map(([sender, body, direction], index) => {
    const sent = new Date(now.getTime() - (input.messages.length - index) * 6 * 60_000);
    return {
      id: `${id}-message-${index + 1}`,
      direction,
      sender,
      body,
      sentAt: new Intl.DateTimeFormat("es-CL", { dateStyle: "short", timeStyle: "short" }).format(sent),
      sentAtIso: sent.toISOString(),
      type: "text" as const,
    };
  });
  const last = messages.at(-1)!;
  return {
    id,
    tenantId: "pgr-seguridad",
    company: "PGR Seguridad",
    customer: input.customer,
    site: input.site,
    contactName: input.contactName,
    phone: "+56 9 0000 0000",
    summary: input.summary,
    lastMessage: last.body,
    lastMessageAt: last.sentAtIso ?? last.sentAt,
    unreadCount: input.messages.filter((message) => message[2] === "inbound").length,
    priority: input.priority,
    status: input.visitRequired ? "waiting" : "open",
    assignedTo: input.assignedTo,
    tags: [...input.tags, "piloto-controlado"],
    commitments: input.commitments.map(([description, responsible, dueAt, sourceIndex], index) => ({
      id: `${id}-commitment-${index + 1}`,
      conversationId: id,
      description,
      responsible,
      dueAt,
      status: "pending" as const,
      sourceMessageId: messages[sourceIndex]?.id ?? messages[0].id,
      createdAt: now.toISOString(),
    })),
    messages,
    source: "demo",
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    nextAction: input.nextAction,
    visitRequired: input.visitRequired,
    reportRequired: input.reportRequired,
    managedFromApp: false,
  };
}

function slug(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}
